import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';

import { Col, Row, Table, notification, Spin, Alert, Skeleton, Button } from "antd";
import { CloseCircleOutlined, InfoCircleOutlined, AppstoreFilled } from "@ant-design/icons";

import { getGapsTemplate } from "../../API/gaps";

import GAPsSearch from "./components/GAPsSearch/GAPsSearch";
import Columns from './components/GAPsTable/columns';

import WithPermission from "../Common/WithPermission/WithPermission";
import ExportButton from '../Common/ExportButton/ExportButton';
import PageTitle from '../Common/PageTitle/PageTitle';
import Assetia from '../Common/Assetia/Assetia';

import { useAuth } from "../Context/AuthContext";

const Gaps = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Permisos
  const { hasPermission, userData } = useAuth();
  const isSuperuser = userData?.is_superuser === true;
  const hasAnyRole = userData?.roles?.length > 0;
  const canGapsRead = userData ? hasPermission("gaps", "read") : false;

  const [api, contextHolder] = notification.useNotification();
  const [gaps, setGaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredData, setFilteredData] = useState([]); 
  const [searchText, setSearchText] = useState('');

  const [searchParams, setSearchParams] = useSearchParams();
  const dashboardStatusFilter = searchParams.get('status');
  const dashboardTypeFilter = searchParams.get('type');
  const isDefeatedFilter = searchParams.get('defeated') === "true";
  //const defeatedCount = gaps.filter(gap => gap.is_defeated).length;

  // Obtener los filtros del Dashboard de la URL
  const assetsFromUrl = searchParams.get('assets')?.split(',').filter(Boolean) || [];
  const areasFromUrl = searchParams.get('areas')?.split(',').filter(Boolean) || [];

  // Filtros activos
  const [activeFilters, setActiveFilters] = useState({});
  // Detectar si los filtros vienen del dashboard (están en la URL inicial)
  const hasUrlFilters = assetsFromUrl.length > 0 || areasFromUrl.length > 0 || dashboardStatusFilter || dashboardTypeFilter;
  // Agregar un estado local para defeated cuando no viene de la URL
  //const [localDefeated, setLocalDefeated] = useState(false);
  const [localDefeated, setLocalDefeated] = useState(isDefeatedFilter);
  // Determinar si estamos en modo defeated (de URL o local)
  const isInDefeatedMode = hasUrlFilters ? isDefeatedFilter : localDefeated;
  // Sincronizar SOLO filtros del dashboard con activeFilters
  useEffect(() => {
    const initialFilters = {};
    
    if (assetsFromUrl.length > 0) {
      initialFilters.asset = assetsFromUrl;
    }
    
    if (areasFromUrl.length > 0) {
      initialFilters.area = areasFromUrl;
    }
    
    if (dashboardStatusFilter) {
      initialFilters.status = [dashboardStatusFilter];
    }
    
    if (dashboardTypeFilter) {
      initialFilters.type = [dashboardTypeFilter];
    }
    
    // Solo setear si hay filtros del dashboard
    if (Object.keys(initialFilters).length > 0) {
      setActiveFilters(initialFilters);
    }
  }, [assetsFromUrl.join(','), areasFromUrl.join(','), dashboardStatusFilter, dashboardTypeFilter]);

  // Modificar el cálculo de defeatedCount para que considere los filtros activos
  const defeatedCount = (() => {
    // Partimos de todos los gaps
    let data = gaps;

    // 1️⃣ Filtramos por GAPs vencidos
    data = data.filter(gap => gap.is_defeated === true);

    // 2️⃣ Aplicar filtros del dashboard (assets y areas de la URL)
    if (assetsFromUrl.length > 0) {
      data = data.filter(gap => {
        const gapAsset = gap.dependency_control?.dependency?.asset?.name;
        return gapAsset && assetsFromUrl.includes(gapAsset);
      });
    }

    if (areasFromUrl.length > 0) {
      data = data.filter(gap => {
        const gapArea = gap.area?.name;
        return gapArea && areasFromUrl.includes(gapArea);
      });
    }

    // 3️⃣ Aplicar filtros activos de la tabla (NUEVO)
    if (activeFilters.asset && activeFilters.asset.length > 0) {
      data = data.filter(gap => {
        const gapAsset = gap.dependency_control?.dependency?.asset?.name;
        return activeFilters.asset.includes(gapAsset);
      });
    }

    if (activeFilters.dependency && activeFilters.dependency.length > 0) {
      data = data.filter(gap => {
        const gapDependency = gap.dependency_control?.dependency?.name;
        return activeFilters.dependency.includes(gapDependency);
      });
    }

    if (activeFilters.priority && activeFilters.priority.length > 0) {
      data = data.filter(gap => {
        const gapPriority = gap.dependency_control?.control?.priority?.name;
        return activeFilters.priority.includes(gapPriority);
      });
    }

    if (activeFilters.type && activeFilters.type.length > 0) {
      data = data.filter(gap => activeFilters.type.includes(gap.type));
    }

    if (activeFilters.status && activeFilters.status.length > 0) {
      data = data.filter(gap => activeFilters.status.includes(gap.status));
    }

    if (activeFilters.area && activeFilters.area.length > 0) {
      data = data.filter(gap => {
        const gapArea = gap.area?.name;
        return activeFilters.area.includes(gapArea);
      });
    }

    if (activeFilters.issue_in_progress && activeFilters.issue_in_progress.length > 0) {
      data = data.filter(gap => activeFilters.issue_in_progress.includes(gap.issue_in_progress));
    }

    if (activeFilters.issues_finished && activeFilters.issues_finished.length > 0) {
      data = data.filter(gap => {
        return activeFilters.issues_finished.some(issue => 
          (gap.issues_finished || []).includes(issue)
        );
      });
    }

    return data.length;
  })();

  // Trackear filtros activos
  const handleTableChange = (pagination, filters) => {
    setActiveFilters(filters);
    
    // Solo modificar URL si hay filtros del dashboard
    if (hasUrlFilters) {
      const newParams = new URLSearchParams();

      // Mantener 'defeated' si existe
      if (isDefeatedFilter) {
        newParams.set('defeated', 'true');
      }

      // Solo persistir assets, areas, status, type (los que pueden venir del dashboard)
      if (filters.asset?.length > 0) {
        newParams.set('assets', filters.asset.join(','));
      }
      if (filters.area?.length > 0) {
        newParams.set('areas', filters.area.join(','));
      }
      if (filters.status?.length > 0) {
        newParams.set('status', filters.status[0]);
      }
      if (filters.type?.length > 0) {
        newParams.set('type', filters.type[0]);
      }

      setSearchParams(newParams);
    }
    // Si NO hay filtros del dashboard, NO tocar la URL
    // Los filtros se mantienen solo en activeFilters (memoria)
  };

  const fetchGaps = async () => {
    setLoading(true);
    try {
      const response = await getGapsTemplate();
      let filtered = response;
      if (isInDefeatedMode) {
        filtered = response.filter(gap => gap.is_defeated === true);
      }
      setGaps(response);
      setFilteredData(filtered);
    } catch {
      api.error({ message: "Error al cargar el template de GAPs" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGaps();
  }, [isInDefeatedMode]);

  const gapsTableFilter = () => {
    // Función helper para aplicar un filtro específico
    const applyFilter = (data, filterKey, filterValues) => {
      if (!filterValues || filterValues.length === 0) return data;
      
      const filterMap = {
        asset: (gap) => gap.dependency_control?.dependency?.asset?.name,
        dependency: (gap) => gap.dependency_control?.dependency?.name,
        priority: (gap) => gap.dependency_control?.control?.priority?.name,
        type: (gap) => gap.type,
        status: (gap) => gap.status,
        area: (gap) => gap.area?.name,
        issue_in_progress: (gap) => gap.issue_in_progress,
        issues_finished: (gap) => gap.issues_finished
      };
      
      const getter = filterMap[filterKey];
      if (!getter) return data;
      
      return data.filter(gap => {
        const value = getter(gap);
        
        // Para issues_finished que es un array
        if (filterKey === 'issues_finished') {
          return filterValues.some(fv => (value || []).includes(fv));
        }
        
        return filterValues.includes(value);
      });
    };

    // Aplicar todos los filtros activos excepto uno a la vez
    // para generar las opciones de ese filtro
    const getFilteredDataForColumn = (excludeColumn) => {
      let data = [...filteredData];
      
      Object.entries(activeFilters).forEach(([key, values]) => {
        if (key !== excludeColumn && values && values.length > 0) {
          data = applyFilter(data, key, values);
        }
      });
      
      return data;
    };

    // Generar opciones para cada filtro basándose en datos pre-filtrados
    const assetsData = getFilteredDataForColumn('asset');
    const gapAssets = Array.from(
      new Map(
        assetsData
          .map(gap => [
            gap.dependency_control?.dependency?.asset?.name, 
            gap.dependency_control?.dependency?.asset?.name
          ])
          .filter(([name]) => name)
      ).values()
    );

    const dependenciesData = getFilteredDataForColumn('dependency');
    const gapDependencies = Array.from(
      new Map(
        dependenciesData
          .map(gap => [
            gap.dependency_control?.dependency?.name, 
            gap.dependency_control?.dependency?.name
          ])
          .filter(([name]) => name)
      ).values()
    );

    const prioritiesData = getFilteredDataForColumn('priority');
    const gapPriorities = Array.from(
      new Map(
        prioritiesData
          .map(gap => [
            gap.dependency_control?.control?.priority?.name,
            gap.dependency_control?.control?.priority
          ])
          .filter(([name]) => name)
      ).values()
    );

    const typesData = getFilteredDataForColumn('type');
    const gapTypes = Array.from(
      new Map(
        typesData
          .map(gap => [gap.type, gap.type])
          .filter(([type]) => type)
      ).values()
    );

    const statusData = getFilteredDataForColumn('status');
    const gapStatus = Array.from(
      new Map(
        statusData
          .map(gap => [gap.status, gap.status])
          .filter(([status]) => status)
      ).values()
    );

    const areasData = getFilteredDataForColumn('area');
    const gapArea = Array.from(
      new Map(
        areasData
          .map(gap => [gap.area?.name, gap.area])
          .filter(([name]) => name)
      ).values()
    );

    const issuesInProgressData = getFilteredDataForColumn('issue_in_progress');
    const gapIssuesInProgress = Array.from(
      new Map(
        issuesInProgressData
          .map(gap => [gap.issue_in_progress, gap.issue_in_progress])
          .filter(([issue]) => issue)
      ).values()
    );

    const issuesFinishedData = getFilteredDataForColumn('issues_finished');
    const gapIssuesFinished = Array.from(
      new Map(
        issuesFinishedData
          .flatMap(gap => (gap.issues_finished || []).map(issue => [issue, issue]))
          .filter(([issue]) => issue)
      ).values()
    );

    return {
      assets: gapAssets.map(name => ({ name })),
      dependencies: gapDependencies.map(name => ({ name })),
      types: gapTypes.map(name => ({ name })),
      statuses: gapStatus.map(name => ({ name })),
      areas: gapArea.map(area => ({ name: area.name })),
      priorities: gapPriorities.map(priority => ({ name: priority.name })),
      issuesInProgress: gapIssuesInProgress.map(name => ({ name })),
      issuesFinished: gapIssuesFinished.map(name => ({ name })),
      dashboardStatusFilter: dashboardStatusFilter || null,
      dashboardTypeFilter: dashboardTypeFilter || null, 
      filteredStatusValue: dashboardStatusFilter ? [dashboardStatusFilter] : [],
      filteredTypeValue: dashboardTypeFilter ? [dashboardTypeFilter] : [], 
      isCombinedFilter: !!dashboardStatusFilter && !!dashboardTypeFilter,
      dashboardAssetsFilter: assetsFromUrl ?? [],
      dashboardAreasFilter: areasFromUrl ?? [],
    };
  };

  return (
    <WithPermission
      isSuperuser={isSuperuser}
      hasAnyRole={hasAnyRole}
      requiredPermission="read"
      currentPermission={canGapsRead}
      redirectTo="/home"
    >
      <>
        {contextHolder}
        <div>
          <div className="button-container">
            <Row justify="start">
              <Col>
                <PageTitle title="GAPs" icon="gaps" />
              </Col>
            </Row>

            <Row justify="start" gutter={[0, 16]}>
              <Col flex={!isInDefeatedMode ? "150px" : "180px"}>
                {loading ? (
                  <Skeleton.Input style={{ width: 200, height: 32 }} active size="small" />
                ) : (
                  (() => {
                    const canInteractWithDefeated = (isSuperuser || (hasAnyRole && canGapsRead)) && defeatedCount > 0;

                    return (
                      <Alert
                        message={
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <span>
                              GAPs Vencidos: {(isSuperuser || (hasAnyRole && canGapsRead)) ? defeatedCount : '-'}
                            </span>
                            {isInDefeatedMode && (
                              <CloseCircleOutlined
                                onClick={(e) => {
                                  e.stopPropagation();
                                  
                                  if (hasUrlFilters) {
                                    // Caso 1: Preservar filtros del dashboard
                                    const params = new URLSearchParams(searchParams);
                                    params.delete('defeated');
                                    navigate({ 
                                      pathname: '/gaps', 
                                      search: params.toString() ? `?${params.toString()}` : '' 
                                    });
                                  } else {
                                    // Caso 2: Solo quitar estado local
                                    setLocalDefeated(false);
                                    // NO tocar la URL ni activeFilters
                                  }
                                }}
                                style={{ marginLeft: 12, color: "red", cursor: "pointer" }}
                              />
                            )}
                          </div>
                        }
                        type={isInDefeatedMode ? "error" : "info"}
                        onClick={() => {
                          if (!canInteractWithDefeated) return;
                          if (!isInDefeatedMode) {
                            if (hasUrlFilters) {
                              // Caso 1: Hay filtros del dashboard, usar URL
                              const params = new URLSearchParams(searchParams);
                              params.set('defeated', 'true');
                              navigate({ 
                                pathname: '/gaps', 
                                search: params.toString() 
                              });
                            } else {
                              // Caso 2: Solo filtros de tabla en memoria, usar estado local
                              setLocalDefeated(true);
                              // NO cambiar la URL, mantener activeFilters intactos
                            }
                          }
                        }}
                        style={{
                          fontSize: "14px",
                          fontWeight: "bold",
                          color: !isInDefeatedMode ? "#006939" : "#cf1322",
                          cursor: canInteractWithDefeated ? "pointer" : "not-allowed",
                          opacity: canInteractWithDefeated ? 1 : 0.5,
                          backgroundColor: !isInDefeatedMode ? "#f5f5f5" : undefined,
                          height: 32,
                          borderColor: !isInDefeatedMode ? "#006939" : undefined
                        }}
                      />
                    );
                  })()
                )}
              </Col>
              <Col flex="auto" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <GAPsSearch
                  gapsData={gaps}
                  searchText={searchText}
                  setSearchText={setSearchText}
                  setFilteredData={setFilteredData}
                />
              </Col>
            </Row>

            <Row justify="end" gutter={[8, 0]}>
              {
                isSuperuser &&
                <Col>
                  <Button 
                    type="primary"
                    className="view-deleted-button"
                    onClick={() => {
                      const params = new URLSearchParams();
                      if (assetsFromUrl.length) {
                        params.set('assets', assetsFromUrl.join(','));
                      }
                      if (areasFromUrl.length) {
                        params.set('areas', areasFromUrl.join(','));
                      }
                      navigate(`/gaps-deleted${params.toString() ? `?${params.toString()}` : ''}`);
                    }}>
                    <AppstoreFilled />
                    Ver Borrados
                  </Button>
                </Col>
              }
              <Col>
                <ExportButton
                  api={api}
                  contextHolder={contextHolder}
                  data={filteredData}
                  filename="Gaps.csv"
                  transformData={(data) =>
                    data.map(item => ({
                      Activo: item.dependency_control?.dependency?.asset?.name || 'N/A',
                      Dependencia: item.dependency_control?.dependency?.name || 'N/A',
                      Control: item.dependency_control?.control?.title || 'N/A',
                      Prioridad: item.dependency_control?.control?.priority?.name || 'N/A',
                      'Tipo de GAP': item.type || 'N/A',
                      'Estado del GAP': item.status || 'N/A',
                      'Issue en curso': item.issue_in_progress || '-',
                      'Issues finalizados': (item.issues_finished || []).join(', ') || '-',
                      'Responsable': item.area?.name || 'N/A'
                    }))
                  }
                />
              </Col>
            </Row>
          </div>

          <Spin className="custom-spin" spinning={loading}>
            <Table
              key={location.search}
              className="custom-pagination"
              columns={Columns(gapsTableFilter())} 
              dataSource={filteredData}
              rowKey="code"
              scroll={{ y: 450 }}
              pagination={{
                defaultPageSize: 30,
                showSizeChanger: true,
                pageSizeOptions: ['30', '50', '100'],
                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`
              }}
              onChange={handleTableChange}
              rowClassName={(record) =>
                record.is_defeated ? "row-defeated" : ""
              }
            />
          </Spin>
        </div>
        <Row style={{ marginTop: 80 }}>
          <Assetia
            pageTitle="GAP"
            pageDescription="ver la lista de todos los GAPs generados"
            view="gaps"
          />
        </Row>
      </>
    </WithPermission>
  );
};

export default Gaps;